import { Alert, Button, Card, Space, Tag, Typography } from 'antd';
import { CheckCircleFilled, RightOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useInstitution } from '../../contexts/InstitutionContext';
import { MockBanner } from '../../pages/components/MockBanner';

/**
 * Admin surface — Screen 1. The setup landing after invitation acceptance.
 * Structure matches Admin.dc.html screen 1: 6-step checklist card left,
 * three side cards right, warm yellow "nothing is live yet" reassurance.
 *
 * All step actions link to Placeholder pages until each setup screen (2–7) ships.
 */
export function SetupPage() {
  const ctx = useInstitution();
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const steps = ctx.setup.steps;

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto' }}>
      <MockBanner when="the setup workflow (Admin screens 1–7)" needs="setup_progress table + step forms" />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Typography.Title level={2} style={{ margin: 0, letterSpacing: '-0.02em' }}>
            Let's get {ctx.institution.name} running
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13.5, maxWidth: 640, display: 'block', marginTop: 6 }}>
            About two hours of work, most of it the import. You can stop and come back — nothing is lost.
          </Typography.Text>
        </div>
        <Space size={8}>
          <Button disabled title="Support scheduling ships later">Book a call with us</Button>
          <Button type="primary" onClick={() => navigate(`/i/${institution}/setup/students`)}>
            Start with students
          </Button>
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 14, alignItems: 'flex-start' }}>
        {/* Left: 6-step checklist */}
        <Card size="small">
          <div style={{ padding: '4px 4px 12px' }}>
            <Typography.Text
              style={{
                fontSize: 10.5,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: '#8a94a6',
                fontWeight: 600,
              }}
            >
              Six steps to live
            </Typography.Text>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {CHECKLIST.map((it, idx) => {
              const status = steps.find((s) => s.step === it.step);
              const done = status?.done ?? false;
              return (
                <SetupRow
                  key={it.step}
                  index={idx + 1}
                  title={it.title}
                  desc={it.desc}
                  why={it.why}
                  time={it.time}
                  action={it.action}
                  href={`/i/${institution}/setup/${it.step.replace(/_/g, '-')}`}
                  done={done}
                />
              );
            })}
          </div>
        </Card>

        {/* Right: side cards */}
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card size="small" title="What we need from you">
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5 }}>
              No IT department, no data cleaning, no CSV template to fill in.
            </Typography.Paragraph>
            <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12.5, color: '#26304a', lineHeight: 1.6 }}>
              <li>Your student list — <b>whatever Excel you already keep</b></li>
              <li>A staff list, with mobile numbers where you have them</li>
              <li>Crest and letterhead, if you want them on print</li>
              <li>Your April fee circular, or whatever you send to guardians</li>
            </ul>
          </Card>

          <Card size="small" title="You are not alone">
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5 }}>
              An implementation person on our side sees your setup progress. If you stall for 24
              hours, they call you. This is how you don't end up two weeks in with an unusable
              database and no one to ask.
            </Typography.Paragraph>
            <Button size="small" style={{ marginTop: 10 }} disabled>
              Book a setup call
            </Button>
          </Card>

          <Alert
            type="warning"
            showIcon
            message="Nothing is live yet"
            description={
              <Typography.Text style={{ fontSize: 12.5, color: '#7d5610' }}>
                No parent can sign in, no message will send, and no fee is billed until you finish{' '}
                <b>step 6</b>. Experiment freely — everything is reversible.
              </Typography.Text>
            }
          />
        </Space>
      </div>

      <div style={{ marginTop: 14 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
          Setup can be paused any time.{' '}
          <Link to={`/i/${institution}/admin`}>Skip to the admin console</Link> to look around
          first — the setup checklist stays available from the settings menu.
        </Typography.Text>
      </div>
    </div>
  );
}

interface ChecklistItem {
  step: 'students' | 'calendar' | 'staff' | 'fees' | 'attendance_mode' | 'invite_guardians';
  title: string;
  desc: string;
  why: string;
  time: string;
  action: string;
}

const CHECKLIST: ChecklistItem[] = [
  {
    step: 'students',
    title: 'Students and guardians',
    desc: 'From your Excel files',
    why: 'The whole product hangs off this. Bring whatever you have — we read the sheet, we do not demand a template.',
    time: '~40 min',
    action: 'Start',
  },
  {
    step: 'calendar',
    title: 'Year and calendar',
    desc: 'Terms, holidays, working days',
    why: 'Every date in the system reads from here. An exam cannot be scheduled on a holiday because the datesheet asks this one.',
    time: '~15 min',
    action: 'Open',
  },
  {
    step: 'staff',
    title: 'Staff and roles',
    desc: 'Who can do what',
    why: 'Invite teachers last — they need the timetable. Build a role like "Clerk" from templates; the code checks permission keys, not names.',
    time: '~20 min',
    action: 'Open',
  },
  {
    step: 'fees',
    title: 'Fee structure',
    desc: 'Heads, amounts, instalments',
    why: 'Nothing is billed until you say so. Refuses to guess — if the circular has no library fee for Class 9, the total says so.',
    time: '~25 min',
    action: 'Open',
  },
  {
    step: 'attendance_mode',
    title: 'How attendance works here',
    desc: 'Daily or period-wise',
    why: 'Can differ by grade. Changeable later — the shape is the same either way.',
    time: '~5 min',
    action: 'Open',
  },
  {
    step: 'invite_guardians',
    title: 'Invite guardians',
    desc: 'Once the rest is right',
    why: 'Deliberately last. A parent’s first impression is worth protecting.',
    time: '~10 min',
    action: 'Open',
  },
];

function SetupRow({
  index,
  title,
  desc,
  why,
  time,
  action,
  href,
  done,
}: {
  index: number;
  title: string;
  desc: string;
  why: string;
  time: string;
  action: string;
  href: string;
  done: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr) 120px',
        gap: 14,
        padding: '14px 4px',
        borderTop: index === 1 ? '0' : '1px solid #f1f1f4',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ paddingTop: 2 }}>
        {done ? (
          <CheckCircleFilled style={{ color: '#1c6442', fontSize: 22 }} />
        ) : (
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: '#f7f7f5',
              border: '1px solid #dcdde3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 11,
              color: '#5b6478',
            }}
          >
            {index}
          </div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, color: '#14213d', fontSize: 14 }}>{title}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {desc}</Typography.Text>
          <Tag style={{ marginLeft: 'auto', marginRight: 0, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
            {time}
          </Tag>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, maxWidth: 640 }}>
          {why}
        </Typography.Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Link to={href}>
          <Button type={index === 1 ? 'primary' : 'default'} size="small">
            {action} <RightOutlined style={{ fontSize: 10 }} />
          </Button>
        </Link>
      </div>
    </div>
  );
}
